<template>
    <cart-template :canBack="!isSourceSale" :hasAlert="isSourceSale" :isPlan="isPlanPath">
        <template #configuration>
            <!-- Navigation Tabs -->
            <cart-navigation
                :smallMargin="isSourceSale || (!isPlanPath && !isPaymentPath)"
                context="upsell"
            />

            <!-- EXPIRATION MESSAGE -->
            <expiration-message
                v-if="isSourceSale && !isPaymentPath"
                :expirationDate="expirationDate"
            ></expiration-message>
            <!-- Current Subscription details -->
            <subscription-summary
                v-if="!isPlanPath && !isPaymentPath"
                v-loading="!previousCart.total.monthly.total_amount_tax_included"
                context="upsell"
            />

            <!-- Tabs Form -->
            <el-form ref="upsellCartForm" labelPosition="top" :rules="setRules" :model="modelForm">
                <router-view store="upsell"></router-view>
            </el-form>
        </template>
        <!-- Summary -->
        <template #resume>
            <cart-summary
                v-loading="!previousCart.total.monthly.total_amount_tax_included"
                context="upsell"
            >
                <template #licences>
                    <ul class="ov-auto mb-0 cart-licences-list">
                        <li
                            v-for="(product, index) in productsWithLicences"
                            :key="product.code"
                            class="flex-row"
                            :class="{
                                'mb-2': index + 1 !== productsWithLicences.length,
                            }"
                        >
                            <div>
                                <p class="grey m-0 fs-3">
                                    {{
                                        $l10n.n__(
                                            '%(total)s licence %(productsName)s',
                                            '%(total)s licences %(productsName)s',
                                            products[product.id].quantity,
                                            {
                                                total: products[product.id].quantity,
                                                productsName: product.title,
                                            },
                                        )
                                    }}
                                </p>
                                <span v-if="freeLicencesByProduct(product.id)" class="grey">{{
                                    $l10n.n__(
                                        'Dont %s offerte',
                                        'Dont %s offertes',
                                        freeLicencesByProduct(product.id),
                                        freeLicencesByProduct(product.id),
                                    )
                                }}</span>
                            </div>
                            <template v-if="products[product.id].total">
                                <p
                                    v-if="freeLicencesByProduct(product.id)"
                                    class="flex-item-right my-0"
                                >
                                    <span class="strike grey semibold">{{
                                        $filters.numberFormatToFloatWithOptions(
                                            products[product.id].total.monthly.total_amount,
                                            numformat,
                                            true,
                                        )
                                    }}</span>
                                    <span class="success-50 fs-3 pl-2 semibold"
                                        >{{
                                            $filters.numberFormatToFloatWithOptions(
                                                products[product.id].total.monthly
                                                    .total_amount_discounted,
                                                numformat,
                                                true,
                                            )
                                        }}<span class="fs-2">{{ $l10n.__('/mois') }}</span></span
                                    >
                                </p>
                                <p v-else class="flex-item-right my-0 fs-3 semibold grey">
                                    {{
                                        $filters.numberFormatToFloatWithOptions(
                                            products[product.id].total.monthly
                                                .total_amount_discounted,
                                            numformat,
                                            true,
                                        )
                                    }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                                </p>
                            </template>
                        </li>
                        <!-- Packs Summary -->
                        <li
                            v-for="(product, index) in [
                                ...packsWithLicences,
                                ...groupedPacksWithLicences,
                            ]"
                            :key="product.code"
                            class="flex-row"
                            :class="{
                                'mb-2':
                                    index + 1 !==
                                    [...packsWithLicences, ...groupedPacksWithLicences].length,
                                'mt-2': index === 0,
                            }"
                        >
                            <div>
                                <p class="grey m-0 fs-3">
                                    {{
                                        $l10n.__('Module %(productsName)s', {
                                            productsName: !!product.groups.length
                                                ? product.printableName
                                                : product.title,
                                        })
                                    }}
                                </p>
                            </div>
                            <template v-if="products[product.id].total">
                                <!-- Discount detail -->
                                <p
                                    v-if="
                                        products[product.id].total.monthly.total_amount_discounted <
                                        products[product.id].total.monthly.total_amount
                                    "
                                    class="flex-item-right my-0 semibold"
                                >
                                    <span class="strike grey semibold">{{
                                        $filters.numberFormatToFloatWithOptions(
                                            products[product.id].total.monthly.total_amount,
                                            numformat,
                                            true,
                                        )
                                    }}</span>
                                    <span class="success-50 fs-3 pl-2">
                                        <template
                                            v-if="
                                                products[product.id].total.monthly
                                                    .total_amount_discounted !== 0
                                            "
                                        >
                                            {{
                                                $filters.numberFormatToFloatWithOptions(
                                                    products[product.id].total.monthly
                                                        .total_amount_discounted,
                                                    numformat,
                                                    true,
                                                )
                                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                                        </template>
                                        <template v-else>{{ $l10n.__('Offert') }}</template>
                                    </span>
                                </p>
                                <!-- Base price -->
                                <p v-else class="flex-item-right my-0 fs-3 semibold grey">
                                    {{
                                        $filters.numberFormatToFloatWithOptions(
                                            products[product.id].total.monthly
                                                .total_amount_discounted,
                                            numformat,
                                            true,
                                        )
                                    }}<span class="fs-2">{{ $l10n.__('/mois') }}</span>
                                </p>
                            </template>
                        </li>
                    </ul>
                </template>
                <!-- Subscription net total after upsell -->
                <template #subtotal>
                    <div class="flex-column fs-3 cart-recap-total-label">
                        <span class="mb-2">{{ $l10n.__('Sous-total') }}</span>
                        <span v-if="taxRate && taxRate !== 0" class="mb-2"
                            >{{ $l10n.__('TVA') }} ({{ taxRate }}%)</span
                        >
                        <span class="bold">{{ $l10n.__('Total') }}</span>
                    </div>
                    <div v-if="isYearly" class="flex-column aright fs-3">
                        <span class="semibold mb-2"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount_discounted,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/an') }}</span></span
                        >
                        <span v-if="taxRate && taxRate !== 0" class="semibold mb-2">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount_vat,
                                    numformat,
                                    true,
                                )
                            }}
                        </span>
                        <span class="bold"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount_tax_included,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/an') }}</span></span
                        >
                    </div>
                    <div v-if="isMonthly" class="flex-column aright fs-3">
                        <span class="semibold mb-2"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalMonthly.total_amount_discounted,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span></span
                        >
                        <span v-if="taxRate && taxRate !== 0" class="semibold mb-2">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalMonthly.total_amount_vat,
                                    numformat,
                                    true,
                                )
                            }}
                        </span>
                        <span class="bold"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalMonthly.total_amount_tax_included,
                                    numformat,
                                    true,
                                )
                            }}<span class="fs-2">{{ $l10n.__('/mois') }}</span></span
                        >
                    </div>
                </template>

                <span class="separator block my-3"></span>
                <!-- Difference between actual and upsell -->
                <article class="flex-row jc-space">
                    <div class="flex-column fs-3 cart-recap-total-label">
                        <span class="mb-3">{{ $l10n.__('Sous-total supplémentaire estimé') }}</span>
                        <span v-if="taxRate && taxRate !== 0" class="mb-3"
                            >{{ $l10n.__('TVA supplémentaire estimée') }} ({{ taxRate }}%)</span
                        >
                    </div>
                    <div v-if="isYearly" class="flex-column aright fs-3">
                        <span class="semibold mb-3"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount_discounted -
                                        previousCart.total.yearly.total_amount_discounted,
                                    numformat,
                                    true,
                                )
                            }}{{ $l10n.__('/an') }}</span
                        >
                        <span v-if="taxRate && taxRate !== 0" class="semibold mb-3">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalYearly.total_amount_vat -
                                        previousCart.total.yearly.total_amount_vat,
                                    numformat,
                                    true,
                                )
                            }}
                        </span>
                    </div>
                    <div v-if="isMonthly" class="flex-column aright fs-3">
                        <span class="semibold mb-3"
                            >{{
                                $filters.numberFormatToFloatWithOptions(
                                    totalMonthly.total_amount_discounted -
                                        previousCart.total.monthly.total_amount_discounted,
                                    numformat,
                                    true,
                                )
                            }}{{ $l10n.__('/mois') }}</span
                        >
                        <span v-if="taxRate && taxRate !== 0" class="semibold mb-3">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalMonthly.total_amount_vat -
                                        previousCart.total.monthly.total_amount_vat,
                                    numformat,
                                    true,
                                )
                            }}
                        </span>
                    </div>
                </article>

                <!-- Total prorated -->
                <template #total>
                    <div class="flex-row jc-space" :class="{ 'mb-4': !isPaymentPath }">
                        <p class="fs-4 my-0 bold cart-recap-total-label--cta">
                            {{ labelTotalTaxIncluded }}
                        </p>
                        <p v-if="isYearly" class="fs-4 my-0 aright bold">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalProrated.yearly.total_tax_included,
                                    numformat,
                                    true,
                                    numformat,
                                    true,
                                )
                            }}
                        </p>
                        <p v-if="isMonthly" class="fs-4 my-0 aright bold">
                            {{
                                $filters.numberFormatToFloatWithOptions(
                                    totalProrated.monthly.total_tax_included,
                                    numformat,
                                    true,
                                    numformat,
                                    true,
                                )
                            }}
                        </p>
                    </div>
                </template>
                <template #actions>
                    <template v-if="isPaymentPath">
                        <span class="separator block my-3"></span>
                        <span class="block fs-2 grey semibold mt-4 mb-2">{{
                            $l10n.__('La prochaine facture sera calculée prorata temporis')
                        }}</span>
                    </template>
                    <CompassButton
                        :label="labelButton"
                        type="primary"
                        size="medium"
                        :disabled="!cartUpselled || computeLoading"
                        :leftIcon="isPaymentPath ? 'lock-fill' : ''"
                        :loading="computeLoading"
                        class="wp-10"
                        @click="submitForm('upsellCartForm', 'nextAction')"
                    />
                </template>
            </cart-summary>
        </template>
    </cart-template>
</template>

<script>
import PricingV4Module from 'stores/sellsyaccountmanager/v4/upsell';
import { createNamespacedHelpers } from 'vuex';
import CartTemplate from 'vcomponents/pages/account/CartTemplate.vue';
import CartSummary from 'vcomponents/pages/account/CartSummary.vue';
import FormMixin from 'mixins/FormMixins';
import AddressRules from 'vcomponents/forms/rules/AddressRules.js';
import VatIdentifierValidator from 'Services/VatIdentifierValidator';
import { checkIsTrue } from 'vcomponents/forms/rules/Rules';
import { goToUrl } from 'js/vue/helpers';
import Card from 'vcomponents/Card.vue';
import { fetchCart } from 'api/sellsyaccountmanager';
import Loader from 'vcomponents/utilities/Loader.vue';
import SubscriptionSummary from './SubscriptionSummary.vue';
import ExpirationMessage from 'vcomponents/pages/account/client/ExpirationMessage.vue';
import CartNavigation from './CartNavigation.vue';

const upsellCart = createNamespacedHelpers('upsellCart');
const user = createNamespacedHelpers('user');
export default {
    mixins: [FormMixin],
    data() {
        return {
            rules: {
                name: [
                    {
                        validator: (rule, value, callback, source, options) => {
                            const contact = {
                                name: this.modelForm.name,
                                forename: this.modelForm.forename,
                                email: this.modelForm.email,
                                phone: this.modelForm.phone,
                            };

                            if (_.some(contact, (info) => _.isNil(info) || !info.length))
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Veuillez renseigner des informations de contact valides',
                                        ),
                                    ),
                                );
                            callback();
                        },
                    },
                ],
                forename: [
                    { required: true, message: this.$l10n.__('Veuillez renseigner un prénom') },
                ],
                phone: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner un numéro de téléphone'),
                    },
                    {
                        min: 4,
                        message: this.$l10n.__('Veuillez renseigner un numéro de téléphone valide'),
                    },
                ],
                email: [
                    {
                        required: true,
                        type: 'email',
                        message: this.$l10n.__('Veuillez renseigner une adresse email'),
                    },
                ],
                products: [
                    {
                        validator: (rule, value, callback) => {
                            /**
                             * Cart must get 2 paid licences or 1 if allowed by permission
                             */
                            if (this.customer.allowsOneLicense && this.quantityPaidLicences < 1) {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Veuillez sélectionner au minimum 1 licence payante',
                                        ),
                                    ),
                                );
                            } else if (
                                !this.customer.allowsOneLicense &&
                                this.quantityPaidLicences < 2
                            ) {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Veuillez sélectionner au minimum 2 licences payantes',
                                        ),
                                    ),
                                );
                            }
                            callback();
                        },
                        trigger: 'change',
                    },
                ],
                billingSettings: {
                    ...AddressRules,
                },
                billingLocale: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner une langue de facturation'),
                    },
                ],
                vatIntra: [
                    {
                        validator: (rule, value, callback) => {
                            /**
                             * Check TVA Intra format with associated country, but no require.
                             */
                            if (
                                value === '' ||
                                _.isNil(value) ||
                                _.isUndefined(this.billingSettings.countryCode)
                            )
                                return callback();

                            if (_.isNull(this.billingSettings.countryCode))
                                return callback(new Error(this.$l10n.__('Aucun pays sélectionné')));

                            const validator = new VatIdentifierValidator(
                                this.billingSettings.countryCode,
                            );
                            if (!validator.isValid(value)) {
                                return callback(
                                    new Error(
                                        this.$l10n.__(
                                            'Le format du n° de tva ne correspond pas à celui du pays sélectionné',
                                        ),
                                    ),
                                );
                            }
                            callback();
                        },
                        trigger: 'change',
                    },
                ],
            },
            cardValidator: {
                cardNumber: [
                    {
                        validator: checkIsTrue,
                        message: this.$l10n.__('Veuillez renseigner un numéro de carte valide'),
                    },
                ],
                cardExpiry: [
                    {
                        validator: checkIsTrue,
                        message: this.$l10n.__('Veuillez renseigner une date valide'),
                    },
                ],
                cardCvc: [
                    {
                        validator: checkIsTrue,
                        message: this.$l10n.__('Veuillez renseigner un code valide'),
                    },
                ],
                cardName: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner le nom sur la carte'),
                    },
                ],
            },
        };
    },
    computed: {
        ...upsellCart.mapGetters([
            'isSourceSale',
            'expirationDate',
            'modelForm',
            'recurrency',
            'products',
            'isYearly',
            'isMonthly',
            'productsWithLicences',
            'packsWithLicences',
            'groupedPacksWithLicences',
            'previousCart',
            'computeLoading',
            'freeLicencesByProduct',
            'totalYearly',
            'totalMonthly',
            'canGoToPayment',
            'billingSettings',
            'isManualMethod',
            'paymentMethodLabel',
            'cartId',
            'taxRate',
            'numformat',
            'customerId',
            'context',
            'totalProrated',
            'isBillingContactPayer',
            'quantityPaidLicences',
            'customer',
        ]),
        ...user.mapState(['locale']),
        isPlanPath() {
            return this.$route.name === 'sam.v4.client.upsell.form.plan';
        },
        isConfigurationPath() {
            return this.$route.name === 'sam.v4.client.upsell.form.configuration';
        },
        isPaymentPath() {
            return this.$route.name === 'sam.v4.client.upsell.form.payment';
        },
        legalInformationLang() {
            const acceptedLang = ['en', 'fr'];
            const userLocale = this.locale.split('_')[0];
            return acceptedLang.includes(userLocale) ? userLocale : 'fr';
        },
        labelButton() {
            return this.isPaymentPath
                ? this.isManualMethod
                    ? this.$l10n.__('Valider la souscription')
                    : this.$l10n.__('Valider le paiement')
                : this.$l10n.__('Continuer');
        },
        nextRoute() {
            const actualRoute = this.$route.name;
            switch (actualRoute) {
                case 'sam.v4.client.upsell.form.plan':
                    return 'sam.v4.client.upsell.form.configuration';
                case 'sam.v4.client.upsell.form.configuration':
                    return 'sam.v4.client.upsell.form.payment';
                default:
                    return false;
            }
        },
        /**
         * Cart total by Sale may have same MRR to current subscription
         */
        cartUpselled() {
            const monthlyIsUpselled =
                this.previousCart.total.monthly.total_amount_discounted !==
                this.totalMonthly.total_amount_discounted;
            const yearlyIsUpselled =
                this.previousCart.total.yearly.total_amount_discounted !==
                this.totalYearly.total_amount_discounted;

            return this.isSourceSale ? true : monthlyIsUpselled && yearlyIsUpselled;
        },
        labelTotalTaxIncluded() {
            return this.isPaymentPath || this.isConfigurationPath
                ? this.$l10n.__("Total estimé dû aujourd'hui")
                : this.$l10n.__('Total');
        },
        /**
         * Set the class name for the underline's effect in nav
         */
        selectRoute() {
            if (this.isPlanPath) return 'plan';
            if (this.isConfigurationPath) return 'config';
            if (this.isPaymentPath) return 'payment';
        },

        setRules() {
            if (!this.isBillingContactPayer) {
                return Object.assign({ ...this.rules }, this.cardValidator);
            } else {
                return this.rules;
            }
        },
    },
    created() {
        this.$store.registerModule('upsellCart', PricingV4Module);
        this.setSource('customer');
        this.setUserType(true);
        this.setCustomerId(this.$store.state.corp.customer);
        this.fetchMetadata(this.$store.state.corp.id);
        this.setCorporationId(this.$store.state.corp.id);
        EventBus.on('PlanCart:submitSelectedPlan', this.nextAction);
    },
    unmounted() {
        EventBus.off('PlanCart:submitSelectedPlan', this.nextAction);
    },
    methods: {
        ...upsellCart.mapMutations([
            'setSource',
            'setCustomerId',
            'setProducts',
            'setComputeLoading',
            'setCorporationId',
            'setCartId',
            'setUserType',
        ]),
        ...upsellCart.mapActions([
            'fetchMetadata',
            'updateProductQuantity',
            'updateRecurrency',
            'updatePlan',
            'computeCart',
            'setCart',
            'setMetadata',
            'updateCart',
            'fetchCustomerData',
            'createCart',
        ]),
        nextAction() {
            if (this.nextRoute) this.continue();
            else this.createUpsellCart();
        },
        continue() {
            this.$router.push({ name: this.nextRoute });
        },
        async createUpsellCart() {
            try {
                if (this.$route.params.upsellCartId !== 'new' || !_.isNil(this.cartId)) {
                    await this.updateCart(this.cartId);
                } else {
                    const cart = await this.createCart();
                    this.setCartId(cart.id);
                }
                if (this.isManualMethod || this.isBillingContactPayer)
                    return goToUrl(
                        this.$router.resolve({ name: 'sam.upsell.payment-confirmed' }).href,
                    );
                EventBus.emit('stripe:create');
            } catch (err) {
                this.setComputeLoading(false);
                Push.error(
                    this.$l10n.__('Une erreur est survenue, veuillez ré-essayer ultérieurement.'),
                );
            }
        },
    },
    beforeUnmount() {
        this.$store.unregisterModule('upsellCart');
    },
    /**
     * Get cart by UUID in params.
     * Configure cart or redirect to error before render component
     */
    async beforeRouteEnter(to, from, next) {
        if (to.params.upsellCartId !== 'new') {
            await fetchCart(to.params.upsellCartId, { user_context: to.meta.user_context })
                .then((response) => {
                    if (
                        response.source === 'sales' &&
                        to.name !== 'sam.v4.client.upsell.form.configuration'
                    )
                        next({
                            name: 'sam.v4.client.upsell.form.configuration',
                            params: { upsellCartId: to.params.upsellCartId },
                        });
                    next((vm) => {
                        vm.setCart(response);
                    });
                })
                .catch((err) => next(false));
        } else {
            next();
        }
    },
    components: {
        CartTemplate,
        CartSummary,
        Card,
        Loader,
        SubscriptionSummary,
        ExpirationMessage,
        CartNavigation,
    },
};
</script>
<style lang="scss" scoped>
.amount {
    align-items: baseline;
    display: flex;
}

.cart-licences-list {
    li {
        margin-bottom: 8px;
        &:last-child {
            margin-bottom: 0;
        }
    }
}

.cart-recap-total-label--cta {
    flex-basis: fit-content;
}

.terms {
    &:deep(.el-checkbox__input) {
        padding-top: 3px;
    }
}
</style>
